/**
 * HeaderNav — the lobby's top bar (stats phase).
 *
 * Drives the static #headerNav shell the way LockerScreen drives #lockerScreen:
 * caches ids, paints the nav items from one table, and reports selections
 * through an injected onSelect. Screens are siblings behind this, not one-off
 * overlays.
 *
 * Two rules the spec is explicit about:
 *  - Account is ABSENT when signed out (never rendered, not disabled).
 *  - The Locker stays reachable by guests exactly as before — and it keeps
 *    id="lockerButton" (plus its #lockerChip) and SplashScreen's 6A
 *    click/lock ownership, so this module paints it but never wires its click.
 */

/** One row per destination. `ownedElsewhere` means another module wires the click. */
const NAV_ITEMS = [
  { id: 'play', label: 'Play', elementId: 'navPlay' },
  { id: 'locker', label: 'Locker', elementId: 'lockerButton', title: 'Skins and trails', chipId: 'lockerChip', ownedElsewhere: true },
  { id: 'account', label: 'Account', elementId: 'navAccount', authOnly: true },
  { id: 'leaderboard', label: 'Leaderboard', elementId: 'navLeaderboard' }
];

const HeaderNav = {
  NAV_ITEMS,
  els: {},
  deps: {},
  user: null,
  activeId: 'play',

  init(deps = {}) {
    this.deps = { onSelect: () => {}, ...deps };
    this.activeId = 'play';
    this.els = {
      root: document.getElementById('headerNav'),
      wordmark: document.getElementById('headerWordmark'),
      items: document.getElementById('headerNavItems'),
      chip: document.getElementById('headerUserChip')
    };
    this.setAuth(deps.user ?? null);
  },

  /** null = signed out: the Account item is not rendered and the chip is empty. */
  setAuth(user) {
    this.user = user || null;
    this.renderItems();
    this.renderChip();
  },

  isAccountVisible() {
    return !!document.getElementById('navAccount');
  },

  renderItems() {
    const { items } = this.els;
    if (!items) return;
    items.innerHTML = '';
    for (const item of NAV_ITEMS) {
      if (item.authOnly && !this.user) continue;     // absent, not disabled (spec: Guests)
      items.appendChild(this.buildItem(item));
    }
    this.setActive(this.activeId);
  },

  buildItem(item) {
    const b = document.createElement('button');
    b.type = 'button';
    b.id = item.elementId;
    b.className = 'nav-item';
    b.dataset.screen = item.id;
    if (item.title) b.title = item.title;
    // The equipped-skin thumbnail lives on the Locker item; SplashScreen.setLockerChip
    // fills it (currently disabled from client.js, but the element stays its seam).
    if (item.chipId) {
      const chip = document.createElement('img');
      chip.id = item.chipId;
      chip.className = 'locker-chip';
      chip.alt = '';
      b.appendChild(chip);
    }
    const label = document.createElement('span');
    label.textContent = item.label;
    b.appendChild(label);
    // The Locker's click and disabled state belong to SplashScreen (6A D1).
    if (!item.ownedElsewhere) b.addEventListener('click', () => this.deps.onSelect(item.id));
    return b;
  },

  renderChip() {
    const { chip } = this.els;
    if (!chip) return;
    chip.innerHTML = '';
    if (!this.user) return;
    if (this.user.avatarUrl) {
      const img = document.createElement('img');
      img.src = this.user.avatarUrl;
      img.alt = '';
      chip.appendChild(img);
    }
    const name = document.createElement('span');
    name.textContent = this.user.displayName || '';
    chip.appendChild(name);
  },

  setActive(screenId) {
    this.activeId = screenId;
    for (const b of this.els.items?.querySelectorAll('.nav-item') || []) {
      const on = b.dataset.screen === screenId;
      b.classList.toggle('active', on);
      if (on) b.setAttribute('aria-current', 'page'); else b.removeAttribute('aria-current');
    }
  },

  show() { this.els.root?.classList.remove('hidden'); },
  hide() { this.els.root?.classList.add('hidden'); }
};

export { HeaderNav, NAV_ITEMS };
